import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';

const Machines = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [machineToDelete, setMachineToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    loadMachines();
  }, []);

  const loadMachines = async () => {
    setLoading(true);
    try {
      const response = await machineryService.getMachines();
      if (response.success) {
        setMachines(response.data);
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'Error al cargar las máquinas',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (machine) => {
    setMachineToDelete(machine);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!machineToDelete) return;

    try {
      const response = await machineryService.deleteMachine(machineToDelete.id);
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Máquina eliminada correctamente',
          type: 'success'
        });
        loadMachines();
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar la máquina',
          type: 'error'
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'Error al eliminar la máquina',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setMachineToDelete(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      maintenance: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-800',
      retired: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.inactive;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Activa',
      maintenance: 'En Mantenimiento',
      inactive: 'Inactiva',
      retired: 'Retirada'
    };
    return labels[status] || status;
  };

  const filteredMachines = machines.filter(machine => {
    const matchesSearch = 
      machine.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      machine.serial_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || machine.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Máquinas</h1>
          <p className="text-gray-600 mt-1">Gestión de maquinaria pesada</p>
        </div>
        <Link
          to="/machinery/machines/create"
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          + Nueva Máquina
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por código, nombre, marca, modelo..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Todos</option>
              <option value="active">Activas</option>
              <option value="maintenance">En Mantenimiento</option>
              <option value="inactive">Inactivas</option>
              <option value="retired">Retiradas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Cargando máquinas...</p>
        </div>
      ) : filteredMachines.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No se encontraron máquinas</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Marca / Modelo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Propio
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMachines.map((machine) => (
                  <tr key={machine.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{machine.code}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{machine.name}</div>
                      <div className="text-sm text-gray-500">S/N: {machine.serial_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{machine.brand}</div>
                      <div className="text-sm text-gray-500">{machine.model}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">{machine.type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(machine.status)}`}>
                        {getStatusLabel(machine.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {machine.propio === 'alquilado' ? 'Alquilado' : 'Propio'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/machinery/machines/${machine.id}/edit`}
                          className="text-teal-600 hover:text-teal-900"
                        >
                          Editar
                        </Link>
                        <button
                          onClick={() => handleDelete(machine)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setMachineToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Eliminar Máquina"
        message={`¿Estás seguro de eliminar la máquina ${machineToDelete?.code} - ${machineToDelete?.name}?`}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Machines;
